"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../../config"
import { useLanguage } from "../../context/LanguageContext"
import { useNotification } from "../../context/NotificationContext"
import "./SystemSettings.css"

const SystemSettings = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const [settings, setSettings] = useState({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeCategory, setActiveCategory] = useState("general")
  const [searchTerm, setSearchTerm] = useState("")
  const [hasChanges, setHasChanges] = useState(false)
  const [pendingChanges, setPendingChanges] = useState({})
  const [expandedSections, setExpandedSections] = useState({})

  const categories = [
    {
      key: "general",
      label: t("General"),
      icon: "fas fa-cog",
      description: t("Basic application settings and configuration"),
    },
    {
      key: "email",
      label: t("Email"),
      icon: "fas fa-envelope",
      description: t("Email service configuration and templates"),
    },
    {
      key: "weather",
      label: t("Weather"),
      icon: "fas fa-cloud-sun",
      description: t("Weather API settings and data sources"),
    },
    {
      key: "calculation",
      label: t("Calculation"),
      icon: "fas fa-calculator",
      description: t("ETref calculation parameters and models"),
    },
    {
      key: "security",
      label: t("Security"),
      icon: "fas fa-shield-alt",
      description: t("Security policies and authentication settings"),
    },
    {
      key: "notification",
      label: t("Notifications"),
      icon: "fas fa-bell",
      description: t("Notification preferences and delivery settings"),
    },
  ]

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/api/system-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.success) {
        setSettings(response.data.data)
        // Initialize expanded sections
        const initialExpanded = {}
        Object.keys(response.data.data).forEach((category) => {
          initialExpanded[category] = true
        })
        setExpandedSections(initialExpanded)
      }
    } catch (error) {
      console.error("Error fetching settings:", error)
      showNotification(t("Error fetching settings"), "error")
    } finally {
      setLoading(false)
    }
  }

  const handleSettingChange = (key, value, dataType) => {
    // Convert value based on data type
    let convertedValue = value
    if (dataType === "number") {
      convertedValue = Number.parseFloat(value) || 0
    } else if (dataType === "boolean") {
      convertedValue = value === "true" || value === true
    }

    setPendingChanges((prev) => ({
      ...prev,
      [key]: convertedValue,
    }))
    setHasChanges(true)
  }

  const saveSettings = async () => {
    try {
      setSaving(true)
      const token = localStorage.getItem("token")

      const updates = Object.entries(pendingChanges).map(([key, value]) => ({
        key,
        value,
      }))

      const response = await axios.put(
        `${API_URL}/api/system-settings`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } },
      )

      if (response.data.success) {
        // Update local settings
        const updatedSettings = { ...settings }
        updates.forEach(({ key, value }) => {
          Object.keys(updatedSettings).forEach((category) => {
            const settingIndex = updatedSettings[category].findIndex((s) => s.key === key)
            if (settingIndex !== -1) {
              updatedSettings[category][settingIndex].value = value
            }
          })
        })

        setSettings(updatedSettings)
        setPendingChanges({})
        setHasChanges(false)
        showNotification(t("Settings saved successfully!"), "success")
      }
    } catch (error) {
      console.error("Error saving settings:", error)
      showNotification(t("Error saving settings"), "error")
    } finally {
      setSaving(false)
    }
  }

  const resetChanges = () => {
    setPendingChanges({})
    setHasChanges(false)
    showNotification(t("Changes reset"), "info")
  }

  const initializeDefaults = async () => {
    if (!window.confirm(t("This will reset all settings to default values. Continue?"))) {
      return
    }

    try {
      const token = localStorage.getItem("token")
      const response = await axios.post(
        `${API_URL}/api/system-settings/initialize`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      if (response.data.success) {
        await fetchSettings()
        showNotification(t("Default settings initialized successfully!"), "success")
      }
    } catch (error) {
      console.error("Error initializing defaults:", error)
      showNotification(t("Error initializing default settings"), "error")
    }
  }

  const exportSettings = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_URL}/api/system-settings/export/all`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `system-settings-${new Date().toISOString().split("T")[0]}.json`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      showNotification(t("Settings exported successfully"), "success")
    } catch (error) {
      console.error("Error exporting settings:", error)
      showNotification(t("Error exporting settings"), "error")
    }
  }

  const toggleSection = (category) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }))
  }

  const renderSettingInput = (setting) => {
    const currentValue = pendingChanges[setting.key] !== undefined ? pendingChanges[setting.key] : setting.value
    const hasChange = pendingChanges[setting.key] !== undefined

    if (!setting.isEditable) {
      return (
        <div className="setting-input-wrapper">
          <input type="text" value={currentValue} disabled className="setting-input disabled" />
          <span className="setting-status readonly">
            <i className="fas fa-lock"></i>
          </span>
        </div>
      )
    }

    let inputElement
    switch (setting.dataType) {
      case "boolean":
        inputElement = (
          <div className="toggle-switch">
            <input
              type="checkbox"
              id={setting.key}
              checked={currentValue === true || currentValue === "true"}
              onChange={(e) => handleSettingChange(setting.key, e.target.checked, setting.dataType)}
              className="toggle-input"
            />
            <label htmlFor={setting.key} className="toggle-label">
              <span className="toggle-slider"></span>
            </label>
          </div>
        )
        break

      case "number":
        inputElement = (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleSettingChange(setting.key, e.target.value, setting.dataType)}
            className="setting-input"
            min={setting.validationRules?.min}
            max={setting.validationRules?.max}
            step={setting.validationRules?.step || "any"}
          />
        )
        break

      case "password":
        inputElement = (
          <input
            type="password"
            value={currentValue}
            onChange={(e) => handleSettingChange(setting.key, e.target.value, setting.dataType)}
            className="setting-input"
            placeholder="••••••••"
          />
        )
        break

      case "textarea":
        inputElement = (
          <textarea
            value={currentValue}
            onChange={(e) => handleSettingChange(setting.key, e.target.value, setting.dataType)}
            className="setting-textarea"
            rows="3"
          />
        )
        break

      default:
        if (setting.validationRules?.options) {
          inputElement = (
            <select
              value={currentValue}
              onChange={(e) => handleSettingChange(setting.key, e.target.value, setting.dataType)}
              className="setting-select"
            >
              {setting.validationRules.options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          )
        } else {
          inputElement = (
            <input
              type="text"
              value={currentValue}
              onChange={(e) => handleSettingChange(setting.key, e.target.value, setting.dataType)}
              className="setting-input"
              pattern={setting.validationRules?.pattern}
            />
          )
        }
    }

    return (
      <div className="setting-input-wrapper">
        {inputElement}
        {hasChange && (
          <span className="setting-status changed">
            <i className="fas fa-circle"></i>
          </span>
        )}
      </div>
    )
  }

  const filteredSettings =
    settings[activeCategory]?.filter(
      (setting) =>
        setting.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
        setting.description.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || []

  if (loading) {
    return (
      <div className="system-settings" dir={direction}>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t("Loading settings...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="system-settings" dir={direction}>
      <div className="settings-header">
        <div className="header-content">
          <div className="header-title">
            <h1>
              <i className="fas fa-cogs"></i>
              {t("System Settings")}
            </h1>
            <p className="header-subtitle">{t("Configure application settings and preferences")}</p>
          </div>

          <div className="header-actions">
            <div className="search-container">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder={t("Search settings...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="action-buttons">
              <button onClick={exportSettings} className="btn btn-outline">
                <i className="fas fa-download"></i>
                {t("Export")}
              </button>
              <button onClick={initializeDefaults} className="btn btn-warning">
                <i className="fas fa-undo"></i>
                {t("Reset to Defaults")}
              </button>
            </div>
          </div>
        </div>

        {hasChanges && (
          <div className="changes-banner">
            <div className="changes-content">
              <div className="changes-info">
                <i className="fas fa-exclamation-circle"></i>
                <span>{t("You have unsaved changes")}</span>
              </div>
              <div className="changes-actions">
                <button onClick={resetChanges} className="btn btn-link">
                  <i className="fas fa-times"></i>
                  {t("Discard")}
                </button>
                <button onClick={saveSettings} className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save"></i>
                      {t("Save Changes")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="settings-content">
        <div className="settings-sidebar">
          <div className="sidebar-header">
            <h3>{t("Categories")}</h3>
          </div>
          <div className="category-list">
            {categories.map((category) => (
              <button
                key={category.key}
                className={`category-item ${activeCategory === category.key ? "active" : ""}`}
                onClick={() => setActiveCategory(category.key)}
              >
                <div className="category-icon">
                  <i className={category.icon}></i>
                </div>
                <div className="category-content">
                  <span className="category-label">{category.label}</span>
                  <span className="category-description">{category.description}</span>
                </div>
                <div className="category-meta">
                  {settings[category.key] && <span className="category-count">{settings[category.key].length}</span>}
                  {pendingChanges &&
                    Object.keys(pendingChanges).some((key) =>
                      settings[category.key]?.some((setting) => setting.key === key),
                    ) && (
                      <span className="category-indicator">
                        <i className="fas fa-circle"></i>
                      </span>
                    )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="settings-main">
          <div className="category-header">
            <div className="category-title">
              <i className={categories.find((c) => c.key === activeCategory)?.icon}></i>
              <div>
                <h2>
                  {categories.find((c) => c.key === activeCategory)?.label} {t("Settings")}
                </h2>
                <p>{categories.find((c) => c.key === activeCategory)?.description}</p>
              </div>
            </div>

            <div className="category-actions">
              <button onClick={() => toggleSection(activeCategory)} className="btn btn-ghost">
                <i className={`fas fa-chevron-${expandedSections[activeCategory] ? "up" : "down"}`}></i>
                {expandedSections[activeCategory] ? t("Collapse") : t("Expand")}
              </button>
            </div>
          </div>

          {expandedSections[activeCategory] && (
            <div className="settings-list">
              {filteredSettings.length === 0 ? (
                <div className="no-settings">
                  <div className="no-settings-icon">
                    <i className="fas fa-search"></i>
                  </div>
                  <h3>{searchTerm ? t("No settings match your search") : t("No settings in this category")}</h3>
                  <p>
                    {searchTerm
                      ? t("Try adjusting your search terms")
                      : t("Settings for this category will appear here when available")}
                  </p>
                </div>
              ) : (
                <div className="settings-grid">
                  {filteredSettings.map((setting) => (
                    <div key={setting.key} className="setting-card">
                      <div className="setting-header">
                        <div className="setting-title">
                          <label className="setting-label">{setting.key}</label>
                          <div className="setting-badges">
                            <span className={`setting-type ${setting.dataType}`}>{setting.dataType}</span>
                            {!setting.isEditable && (
                              <span className="setting-readonly">
                                <i className="fas fa-lock"></i>
                                {t("Read Only")}
                              </span>
                            )}
                            {setting.validationRules?.required && (
                              <span className="setting-required">
                                <i className="fas fa-asterisk"></i>
                                {t("Required")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="setting-body">
                        <p className="setting-description">{setting.description}</p>

                        {setting.validationRules && (
                          <div className="setting-validation">
                            {setting.validationRules.min !== undefined && (
                              <span className="validation-rule">
                                <i className="fas fa-arrow-down"></i>
                                {t("Min")}: {setting.validationRules.min}
                              </span>
                            )}
                            {setting.validationRules.max !== undefined && (
                              <span className="validation-rule">
                                <i className="fas fa-arrow-up"></i>
                                {t("Max")}: {setting.validationRules.max}
                              </span>
                            )}
                            {setting.validationRules.pattern && (
                              <span className="validation-rule">
                                <i className="fas fa-code"></i>
                                {t("Pattern")}: {setting.validationRules.pattern}
                              </span>
                            )}
                          </div>
                        )}

                        <div className="setting-control">{renderSettingInput(setting)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SystemSettings
